import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search, X, Mountain } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ProductGridSkeleton } from "@/components/Skeletons";

const SORTS = [
  { key: "", label: "الافتراضي" },
  { key: "price_asc", label: "السعر: من الأقل" },
  { key: "price_desc", label: "السعر: من الأعلى" },
  { key: "rating", label: "الأعلى تقييماً" },
];

const TAGS = [
  { key: "bestseller", label: "الأكثر طلباً" },
  { key: "new", label: "جديد" },
];

export default function Store() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [geoSources, setGeoSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState(params.get("q") || "");

  const category = params.get("category") || "";
  const geo = params.get("geo") || "";
  const q = params.get("q") || "";
  const tag = params.get("tag") || "";
  const sort = params.get("sort") || "";
  const natural = params.get("natural") === "1";
  const organic = params.get("organic") === "1";
  const maxPrice = params.get("max_price") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/geo-sources").then((r) => setGeoSources(r.data));
  }, []);

  // restore last filters when arriving at a bare /store, and remember every change
  useEffect(() => {
    if ([...params.keys()].length === 0) {
      try {
        const saved = localStorage.getItem("suhool_store_filters");
        if (saved) setParams(new URLSearchParams(saved), { replace: true });
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const s = params.toString();
      if (s) localStorage.setItem("suhool_store_filters", s);
      else localStorage.removeItem("suhool_store_filters");
    } catch { /* ignore */ }
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (geo) p.geo_source = geo;
    if (q) p.q = q;
    if (tag) p.tag = tag;
    if (sort) p.sort = sort;
    if (natural) p.natural = true;
    if (organic) p.organic = true;
    if (maxPrice) p.max_price = maxPrice;
    api.get("/products", { params: p }).then((r) => { setProducts(r.data); setLoading(false); });
  }, [category, geo, q, tag, sort, natural, organic, maxPrice]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (val === "" || val === false || val == null) next.delete(key);
    else next.set(key, val === true ? "1" : val);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const activeCount = [category, geo, tag, natural && "1", organic && "1", maxPrice].filter(Boolean).length;

  const heading = useMemo(() => {
    if (q) return `نتائج البحث عن: "${q}"`;
    if (category) return categories.find((c) => c.key === category)?.name_ar || "المتجر";
    if (geo) return `منتجات ${geoSources.find((g) => g.key === geo)?.name_ar || ""}`;
    return "جميع المنتجات";
  }, [q, category, geo, categories, geoSources]);

  return (
    <div className="min-h-screen pt-24">
      {/* Header band */}
      <div className="border-b border-olive/10 bg-sand/30">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <h1 data-testid="store-heading" className="font-display text-3xl font-bold text-ink sm:text-4xl">{heading}</h1>
          <p className="mt-2 text-ink/55">{loading ? "جارِ التحميل..." : `${products.length} منتج`}</p>

          <form onSubmit={(e) => { e.preventDefault(); update("q", search.trim()); }} className="mt-6 flex max-w-xl items-center gap-2 rounded-full border border-olive/15 bg-white px-4 py-2.5">
            <Search className="h-5 w-5 text-olive" />
            <input data-testid="store-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث بالاسم، المكوّن، المصدر... (مثال: زيت نابلس)" className="w-full bg-transparent text-sm outline-none" />
            {search && <button type="button" onClick={() => { setSearch(""); update("q", ""); }}><X className="h-4 w-4 text-ink/40" /></button>}
          </form>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl gap-8 px-6 py-10">
        {/* Filters sidebar */}
        <aside className={`${showFilters ? "fixed inset-0 z-50 overflow-y-auto bg-ivory p-6" : "hidden"} lg:static lg:block lg:w-64 lg:shrink-0 lg:p-0`}>
          <div className="mb-6 flex items-center justify-between lg:mb-4">
            <h3 className="font-display text-lg font-bold">التصفية</h3>
            <div className="flex items-center gap-3">
              {activeCount > 0 && <button onClick={clearAll} className="text-sm text-palred">مسح ({activeCount})</button>}
              <button className="lg:hidden" onClick={() => setShowFilters(false)}><X className="h-6 w-6" /></button>
            </div>
          </div>

          <FilterGroup title="التصنيف (حسب نوع المنتج)">
            {categories.map((c) => (
              <FilterChip key={c.key} active={category === c.key} onClick={() => update("category", category === c.key ? "" : c.key)}
                testid={`filter-category-${c.key}`}>
                {c.name_ar} <span className="text-ink/40">({c.count})</span>
              </FilterChip>
            ))}
          </FilterGroup>

          <FilterGroup title="المصدر الجغرافي">
            {geoSources.map((g) => (
              <FilterChip key={g.key} active={geo === g.key} onClick={() => update("geo", geo === g.key ? "" : g.key)}
                testid={`filter-geo-${g.key}`}>
                <Mountain className="h-3.5 w-3.5" /> {g.name_ar} <span className="text-ink/40">({g.count})</span>
              </FilterChip>
            ))}
          </FilterGroup>

          <FilterGroup title="خصائص">
            <label className="flex cursor-pointer items-center gap-2 py-1 text-sm" data-testid="filter-natural">
              <input type="checkbox" checked={natural} onChange={(e) => update("natural", e.target.checked)} className="accent-olive" /> طبيعي
            </label>
            <label className="flex cursor-pointer items-center gap-2 py-1 text-sm" data-testid="filter-organic">
              <input type="checkbox" checked={organic} onChange={(e) => update("organic", e.target.checked)} className="accent-olive" /> عضوي (موثّق)
            </label>
          </FilterGroup>

          <FilterGroup title="السعر الأقصى">
            <input type="range" min="5" max="40" value={maxPrice || 40} onChange={(e) => update("max_price", e.target.value)}
              className="w-full accent-olive" data-testid="filter-price" />
            <div className="text-sm text-ink/60">حتى ${maxPrice || 40}</div>
          </FilterGroup>

          <FilterGroup title="وسوم">
            {TAGS.map((tg) => (
              <FilterChip key={tg.key} active={tag === tg.key} onClick={() => update("tag", tag === tg.key ? "" : tg.key)}>{tg.label}</FilterChip>
            ))}
          </FilterGroup>
        </aside>

        {/* Grid */}
        <div className="flex-1">
          <div className="mb-6 flex items-center justify-between gap-3">
            <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-toggle"
              className="flex items-center gap-2 rounded-full border border-olive/20 bg-white px-4 py-2 text-sm font-medium lg:hidden">
              <SlidersHorizontal className="h-4 w-4" /> التصفية {activeCount > 0 && `(${activeCount})`}
            </button>
            <select data-testid="sort-select" value={sort} onChange={(e) => update("sort", e.target.value)}
              className="mr-auto rounded-full border border-olive/20 bg-white px-4 py-2 text-sm outline-none">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          {loading ? (
            <ProductGridSkeleton count={6} gridClass="grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-6" />
          ) : products.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-olive/20 py-24 text-center">
              <p className="text-lg text-ink/60">لا توجد منتجات مطابقة.</p>
              <button onClick={clearAll} className="mt-4 rounded-full bg-olive px-6 py-2.5 text-ivory">مسح التصفية</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-6">
              {products.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div className="mb-6 border-b border-olive/10 pb-5">
      <h4 className="mb-3 font-display text-sm font-semibold text-ink/80">{title}</h4>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors ${
        active ? "border-olive bg-olive text-ivory" : "border-olive/20 bg-white text-ink/70 hover:border-olive/50"}`}>
      {children}
    </button>
  );
}
